// The volume policy for the pulled variables the registry declares as unbounded. This stands in for
// summarising a long value down to a representative slice: that needs a summariser, which does not
// exist yet, so until then an
// over-long value is cut and the cut is *reported* rather than made silently. The point of the
// grounding breakdown is that nothing goes missing without the author being told, so a cap that hides
// itself would be worse than no cap at all.
//
// 8,000 characters is roughly 2,000 tokens — enough for a genuinely long task description to survive
// intact, small enough that a pathological one cannot swamp the run. Revisit the number once values
// are summarised rather than cut.
export const NOVA_DESCRIPTION_CHARACTER_CAP = 8000;

// Deliberately the same 20 as `SOFT_CAP_COUNT` in agentic-workflow's `loadTaskContext.ts`, which
// already warns above this many task attachments. Mirroring the platform's existing number keeps one
// answer to "how many files is too many" rather than inventing a second.
export const NOVA_ATTACHED_FILES_ENTRY_CAP = 20;

export const NovaCapUnit = {
	Characters: 'CHARACTERS',
	Entries: 'ENTRIES',
} as const;
export type NovaCapUnit = (typeof NovaCapUnit)[keyof typeof NovaCapUnit];

export interface NovaCapResult {
	// The value as it would enter assembled context, cut if it was over cap.
	value: string;
	// Sizes in `unit`, so the breakdown can show "12,400 → 8,000 characters" or "23 → 20 files".
	originalSize: number;
	cappedSize: number;
	wasCapped: boolean;
	unit: NovaCapUnit;
}

const CHARACTER_CAPPED_TOKENS: string[] = ['{task_description}', '{project_description}'];
const ENTRY_CAPPED_TOKEN = '{attached_files}';
const ENTRY_SEPARATOR = '\n';

function capEntries(value: string, cap: number): NovaCapResult {
	// An empty value is no entries, not one empty entry — `''.split('\n')` would say otherwise.
	const entries = value === '' ? [] : value.split(ENTRY_SEPARATOR);
	const kept = entries.slice(0, cap);
	return {
		value: kept.join(ENTRY_SEPARATOR),
		originalSize: entries.length,
		cappedSize: kept.length,
		wasCapped: entries.length > cap,
		unit: NovaCapUnit.Entries,
	};
}

function capCharacters(value: string, cap: number): NovaCapResult {
	const kept = value.slice(0, cap);
	return {
		value: kept,
		originalSize: value.length,
		cappedSize: kept.length,
		wasCapped: value.length > cap,
		unit: NovaCapUnit.Characters,
	};
}

function uncapped(value: string): NovaCapResult {
	return {
		value,
		originalSize: value.length,
		cappedSize: value.length,
		wasCapped: false,
		unit: NovaCapUnit.Characters,
	};
}

// Applies the volume policy for `token` to `value`. A token with no declared volume concern — a task
// title, a project or client name, a file reference — is returned untouched however long it is: the
// registry calls those length-bounded, and second-guessing that here would report a truncation the
// real assembler will not perform.
export function capVariableValue(token: string, value: string): NovaCapResult {
	if (token === ENTRY_CAPPED_TOKEN) {
		return capEntries(value, NOVA_ATTACHED_FILES_ENTRY_CAP);
	}
	if (CHARACTER_CAPPED_TOKENS.includes(token)) {
		return capCharacters(value, NOVA_DESCRIPTION_CHARACTER_CAP);
	}
	return uncapped(value);
}
